import {
  ProtocolError,
  MAX_OBJECT_BYTES,
  canonicalPhysicalChannels,
  concatBytes,
  decodeKeyConfirmationMessage,
  decodeReceiverKeyResponse,
  decodeSenderDiscoveryOffer,
  encodeKeyConfirmationContext,
  encodeKeyConfirmationMessage,
  encodeReceiverKeyResponse,
  encodeSenderDiscoveryOffer,
  equalBytes,
  hexToBytes,
  utf8,
  type Direction,
  type KeyConfirmationMessage,
  type PhysicalChannel,
  type ReceiverKeyResponse,
  type SenderDiscoveryOffer,
  type SequenceReplayWindow,
} from '@spectrabus/protocol';

const TRANSCRIPT_DOMAIN = utf8('SpectraBus/1/split-peer-transcript');
const SESSION_KEYS_DOMAIN = utf8('SpectraBus/1/split-peer-session-keys');
const KEY_FINGERPRINT_DOMAIN = utf8('SpectraBus/1/key-fingerprint');
const CONFIRMATION_KEY_DOMAIN = utf8('SpectraBus/1/key-confirmation-key');
const CONFIRMATION_MESSAGE_DOMAIN = utf8('SpectraBus/1/key-confirmation-message');
const DISCOVERY_OBJECT_DOMAIN = utf8('SpectraBus/1/discovery-object');
const P256_PUBLIC_KEY_BYTES = 65;
const SESSION_KEY_BYTES = 32;

export type SessionHandshakeFailure =
  | 'unknown-state'
  | 'binding-mismatch'
  | 'key-agreement'
  | 'confirmation-tag'
  | 'direction-mismatch'
  | 'channel-not-negotiated';

export class SessionHandshakeError extends ProtocolError {
  public readonly failure: SessionHandshakeFailure;

  public constructor(failure: SessionHandshakeFailure, message: string) {
    super('SESSION_HANDSHAKE_FAILURE', message);
    this.name = 'SessionHandshakeError';
    this.failure = failure;
  }
}

export interface SenderHandshakeParameters {
  readonly sessionId: string;
  readonly transferId: string;
  readonly discoveryCommitment: string;
  readonly offerSequence: number;
  readonly channels: readonly PhysicalChannel[];
}

export interface SenderHandshakeState {
  readonly role: 'sender';
  readonly offer: SenderDiscoveryOffer;
  readonly offerBytes: Uint8Array;
}

export interface SenderOfferExpectation {
  readonly sessionId?: string;
  readonly transferId?: string;
  readonly discoveryCommitment?: string;
  readonly offerSequence?: number;
  readonly channels?: readonly PhysicalChannel[];
}

export interface ReceiverHandshakeParameters {
  readonly responseSequence: number;
  readonly expectation?: SenderOfferExpectation;
}

export interface SplitPeerSession {
  readonly role: 'sender' | 'receiver';
  readonly sessionId: string;
  readonly transferId: string;
  readonly discoveryCommitment: string;
  readonly channels: readonly PhysicalChannel[];
  readonly senderPublicKey: Uint8Array;
  readonly receiverPublicKey: Uint8Array;
  readonly transcriptHash: string;
  readonly keyFingerprint: string;
  readonly objectRootKey: Uint8Array;
}

export interface ReceiverHandshakeResult {
  readonly offer: SenderDiscoveryOffer;
  readonly response: ReceiverKeyResponse;
  readonly responseBytes: Uint8Array;
  readonly session: SplitPeerSession;
}

export interface DiscoveryObjectSummary {
  readonly name: string;
  readonly mimeType: string;
  readonly byteLength: number;
  readonly objectHash: string;
  readonly chunkSize: number;
  readonly compression: 'none';
}

export interface KeyConfirmationExpectation {
  readonly direction: Direction;
  readonly channel: PhysicalChannel;
  readonly sequence?: number;
  readonly replayWindow?: SequenceReplayWindow;
}

interface SenderPrivateState {
  readonly privateKey: CryptoKey;
  readonly offer: SenderDiscoveryOffer;
  readonly offerBytes: Uint8Array;
}

const senderPrivateStates = new WeakMap<SenderHandshakeState, SenderPrivateState>();
const confirmationRoots = new WeakMap<SplitPeerSession, Uint8Array>();

function subtle(): SubtleCrypto {
  if (globalThis.crypto?.subtle === undefined) {
    throw new ProtocolError('WEB_CRYPTO_UNAVAILABLE', 'Web Crypto is required for SpectraBus.');
  }
  return globalThis.crypto.subtle;
}

function ownedBuffer(bytes: Uint8Array): ArrayBuffer {
  const owned = new Uint8Array(bytes.length);
  owned.set(bytes);
  return owned.buffer;
}

function uint32(value: number): Uint8Array {
  const bytes = new Uint8Array(4);
  new DataView(bytes.buffer).setUint32(0, value);
  return bytes;
}

async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await subtle().digest('SHA-256', ownedBuffer(bytes)));
}

export async function createDiscoveryCommitment(
  summary: DiscoveryObjectSummary,
): Promise<string> {
  const nameBytes = utf8(summary.name);
  const mimeBytes = utf8(summary.mimeType);
  if (
    nameBytes.length < 1 ||
    nameBytes.length > 1024 ||
    mimeBytes.length < 1 ||
    mimeBytes.length > 255 ||
    !Number.isSafeInteger(summary.byteLength) ||
    summary.byteLength < 0 ||
    summary.byteLength > MAX_OBJECT_BYTES ||
    !Number.isInteger(summary.chunkSize) ||
    summary.chunkSize < 1024 ||
    summary.chunkSize > 128 * 1024 ||
    !/^[\da-f]{64}$/u.test(summary.objectHash) ||
    summary.compression !== 'none'
  ) {
    throw new ProtocolError(
      'DISCOVERY_OBJECT_BOUNDS',
      'Discovery object summary is non-canonical or exceeds negotiated browser bounds.',
    );
  }
  const canonicalSummary = utf8(
    JSON.stringify({
      byteLength: summary.byteLength,
      chunkSize: summary.chunkSize,
      compression: summary.compression,
      mimeType: summary.mimeType,
      name: summary.name,
      objectHash: summary.objectHash,
    }),
  );
  return hex(await sha256(concatBytes(DISCOVERY_OBJECT_DOMAIN, uint32(canonicalSummary.length), canonicalSummary)));
}

function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}

async function generateP256KeyPair(): Promise<CryptoKeyPair> {
  // The ephemeral private key is only ever used for deriveBits. Keeping it
  // non-extractable preserves the WebCrypto opaque-handle boundary: nothing
  // that later obtains this CryptoKey reference can export the raw scalar.
  return (await subtle().generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    ['deriveBits'],
  )) as CryptoKeyPair;
}

async function exportPublicKey(key: CryptoKey): Promise<Uint8Array> {
  const bytes = new Uint8Array(await subtle().exportKey('raw', key));
  if (bytes.length !== P256_PUBLIC_KEY_BYTES || bytes[0] !== 0x04) {
    throw new SessionHandshakeError(
      'key-agreement',
      'Web Crypto returned a non-canonical P-256 public key.',
    );
  }
  return bytes;
}

async function importPublicKey(bytes: Uint8Array): Promise<CryptoKey> {
  try {
    return await subtle().importKey(
      'raw',
      ownedBuffer(bytes),
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      [],
    );
  } catch {
    throw new SessionHandshakeError(
      'key-agreement',
      'Peer public key is not a valid point on the negotiated P-256 curve.',
    );
  }
}

async function deriveSharedSecret(
  privateKey: CryptoKey,
  peerPublicKey: Uint8Array,
): Promise<Uint8Array> {
  try {
    return new Uint8Array(
      await subtle().deriveBits(
        { name: 'ECDH', public: await importPublicKey(peerPublicKey) },
        privateKey,
        256,
      ),
    );
  } catch (error) {
    if (error instanceof SessionHandshakeError) throw error;
    throw new SessionHandshakeError('key-agreement', 'Ephemeral P-256 key agreement failed.');
  }
}

async function transcriptHash(offerBytes: Uint8Array, responseBytes: Uint8Array): Promise<Uint8Array> {
  return sha256(
    concatBytes(
      TRANSCRIPT_DOMAIN,
      uint32(offerBytes.length),
      offerBytes,
      uint32(responseBytes.length),
      responseBytes,
    ),
  );
}

async function deriveSession(
  role: SplitPeerSession['role'],
  offer: SenderDiscoveryOffer,
  response: ReceiverKeyResponse,
  offerBytes: Uint8Array,
  responseBytes: Uint8Array,
  sharedSecret: Uint8Array,
): Promise<SplitPeerSession> {
  const transcript = await transcriptHash(offerBytes, responseBytes);
  let material: CryptoKey;
  try {
    material = await subtle().importKey('raw', ownedBuffer(sharedSecret), 'HKDF', false, [
      'deriveBits',
    ]);
  } finally {
    sharedSecret.fill(0);
  }
  const sessionMaterial = new Uint8Array(
    await subtle().deriveBits(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: ownedBuffer(transcript),
        info: ownedBuffer(SESSION_KEYS_DOMAIN),
      },
      material,
      SESSION_KEY_BYTES * 2 * 8,
    ),
  );
  const objectRootKey = sessionMaterial.slice(0, SESSION_KEY_BYTES);
  const confirmationRoot = sessionMaterial.slice(SESSION_KEY_BYTES);
  sessionMaterial.fill(0);
  const keyFingerprint = await sha256(
    concatBytes(KEY_FINGERPRINT_DOMAIN, transcript, objectRootKey),
  );
  const session: SplitPeerSession = Object.freeze({
    role,
    sessionId: offer.sessionId,
    transferId: offer.transferId,
    discoveryCommitment: offer.discoveryCommitment,
    channels: Object.freeze([...offer.channels]),
    senderPublicKey: offer.senderPublicKey.slice(),
    receiverPublicKey: response.receiverPublicKey.slice(),
    transcriptHash: hex(transcript),
    keyFingerprint: hex(keyFingerprint),
    objectRootKey,
  });
  confirmationRoots.set(session, confirmationRoot);
  return session;
}

function sameChannels(left: readonly PhysicalChannel[], right: readonly PhysicalChannel[]): boolean {
  const canonicalLeft = canonicalPhysicalChannels(left);
  const canonicalRight = canonicalPhysicalChannels(right);
  return (
    canonicalLeft.length === canonicalRight.length &&
    canonicalLeft.every((channel, index) => channel === canonicalRight[index])
  );
}

function assertOfferExpectation(
  offer: SenderDiscoveryOffer,
  expectation: SenderOfferExpectation | undefined,
): void {
  if (expectation === undefined) return;
  if (
    (expectation.sessionId !== undefined && expectation.sessionId !== offer.sessionId) ||
    (expectation.transferId !== undefined && expectation.transferId !== offer.transferId) ||
    (expectation.discoveryCommitment !== undefined &&
      expectation.discoveryCommitment !== offer.discoveryCommitment) ||
    (expectation.offerSequence !== undefined && expectation.offerSequence !== offer.sequence) ||
    (expectation.channels !== undefined && !sameChannels(expectation.channels, offer.channels))
  ) {
    throw new SessionHandshakeError(
      'binding-mismatch',
      'Sender discovery offer does not match the receiver expectation.',
    );
  }
}

export async function createSenderHandshake(
  parameters: SenderHandshakeParameters,
): Promise<SenderHandshakeState> {
  const pair = await generateP256KeyPair();
  const offer: SenderDiscoveryOffer = {
    kind: 'sender-discovery-offer',
    sessionId: parameters.sessionId,
    transferId: parameters.transferId,
    discoveryCommitment: parameters.discoveryCommitment,
    sequence: parameters.offerSequence,
    channels: canonicalPhysicalChannels(parameters.channels),
    senderPublicKey: await exportPublicKey(pair.publicKey),
  };
  const offerBytes = encodeSenderDiscoveryOffer(offer);
  const state: SenderHandshakeState = Object.freeze({
    role: 'sender',
    offer: Object.freeze({ ...offer, senderPublicKey: offer.senderPublicKey.slice() }),
    offerBytes: offerBytes.slice(),
  });
  senderPrivateStates.set(state, {
    privateKey: pair.privateKey,
    offer,
    offerBytes,
  });
  return state;
}

export async function acceptSenderHandshake(
  encodedOffer: Uint8Array,
  parameters: ReceiverHandshakeParameters,
): Promise<ReceiverHandshakeResult> {
  const offerBytes = encodedOffer.slice();
  const offer = decodeSenderDiscoveryOffer(offerBytes);
  assertOfferExpectation(offer, parameters.expectation);
  const pair = await generateP256KeyPair();
  const response: ReceiverKeyResponse = {
    kind: 'receiver-key-response',
    sessionId: offer.sessionId,
    transferId: offer.transferId,
    offerHash: hex(await sha256(offerBytes)),
    sequence: parameters.responseSequence,
    channels: offer.channels,
    receiverPublicKey: await exportPublicKey(pair.publicKey),
  };
  const responseBytes = encodeReceiverKeyResponse(response);
  const sharedSecret = await deriveSharedSecret(pair.privateKey, offer.senderPublicKey);
  const session = await deriveSession(
    'receiver',
    offer,
    response,
    offerBytes,
    responseBytes,
    sharedSecret,
  );
  return {
    offer,
    response,
    responseBytes,
    session,
  };
}

export async function completeSenderHandshake(
  state: SenderHandshakeState,
  encodedResponse: Uint8Array,
): Promise<SplitPeerSession> {
  const privateState = senderPrivateStates.get(state);
  if (privateState === undefined) {
    throw new SessionHandshakeError(
      'unknown-state',
      'Sender handshake state is unknown, completed, or was reconstructed without its private key.',
    );
  }
  const responseBytes = encodedResponse.slice();
  const response = decodeReceiverKeyResponse(responseBytes);
  const expectedOfferHash = await sha256(privateState.offerBytes);
  if (
    response.sessionId !== privateState.offer.sessionId ||
    response.transferId !== privateState.offer.transferId ||
    !equalBytes(hexToBytes(response.offerHash), expectedOfferHash) ||
    !sameChannels(response.channels, privateState.offer.channels)
  ) {
    throw new SessionHandshakeError(
      'binding-mismatch',
      'Receiver key response is not bound to the exact sender discovery offer.',
    );
  }
  const sharedSecret = await deriveSharedSecret(
    privateState.privateKey,
    response.receiverPublicKey,
  );
  const session = await deriveSession(
    'sender',
    privateState.offer,
    response,
    privateState.offerBytes,
    responseBytes,
    sharedSecret,
  );
  senderPrivateStates.delete(state);
  return session;
}

function assertConfirmationTransport(
  session: SplitPeerSession,
  direction: Direction,
  channel: PhysicalChannel,
  localOperation: 'send' | 'receive',
): void {
  const expectedDirection =
    session.role === 'sender' ? 'sender-to-receiver' : 'receiver-to-sender';
  const permittedDirection =
    localOperation === 'send'
      ? expectedDirection
      : expectedDirection === 'sender-to-receiver'
        ? 'receiver-to-sender'
        : 'sender-to-receiver';
  if (direction !== permittedDirection) {
    throw new SessionHandshakeError(
      'direction-mismatch',
      `The ${session.role} peer cannot ${localOperation} a ${direction} key confirmation.`,
    );
  }
  if (!session.channels.includes(channel)) {
    throw new SessionHandshakeError(
      'channel-not-negotiated',
      `Key confirmation channel ${channel} was not negotiated by this session.`,
    );
  }
}

async function confirmationKey(
  session: SplitPeerSession,
  direction: Direction,
  channel: PhysicalChannel,
): Promise<CryptoKey> {
  const root = confirmationRoots.get(session);
  if (root === undefined) {
    throw new SessionHandshakeError(
      'unknown-state',
      'Split-peer session is unknown or was reconstructed without its confirmation key.',
    );
  }
  const material = await subtle().importKey('raw', ownedBuffer(root), 'HKDF', false, ['deriveKey']);
  return subtle().deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: ownedBuffer(hexToBytes(session.transcriptHash)),
      info: ownedBuffer(concatBytes(CONFIRMATION_KEY_DOMAIN, utf8(direction), utf8(channel))),
    },
    material,
    { name: 'HMAC', hash: 'SHA-256', length: 256 },
    false,
    ['sign', 'verify'],
  );
}

export async function createKeyConfirmation(
  session: SplitPeerSession,
  direction: Direction,
  channel: PhysicalChannel,
  sequence: number,
): Promise<Uint8Array> {
  assertConfirmationTransport(session, direction, channel, 'send');
  const context = {
    kind: 'key-confirmation' as const,
    sessionId: session.sessionId,
    transcriptHash: session.transcriptHash,
    direction,
    channel,
    sequence,
  };
  const contextBytes = encodeKeyConfirmationContext(context);
  const confirmationTag = new Uint8Array(
    await subtle().sign(
      'HMAC',
      await confirmationKey(session, direction, channel),
      ownedBuffer(concatBytes(CONFIRMATION_MESSAGE_DOMAIN, contextBytes)),
    ),
  );
  return encodeKeyConfirmationMessage({ ...context, confirmationTag });
}

export async function verifyKeyConfirmation(
  session: SplitPeerSession,
  encodedMessage: Uint8Array,
  expectation: KeyConfirmationExpectation,
): Promise<KeyConfirmationMessage> {
  assertConfirmationTransport(session, expectation.direction, expectation.channel, 'receive');
  const message = decodeKeyConfirmationMessage(encodedMessage);
  const contextBytes = encodeKeyConfirmationContext(message);
  const validTag = await subtle().verify(
    'HMAC',
    await confirmationKey(session, message.direction, message.channel),
    ownedBuffer(message.confirmationTag),
    ownedBuffer(concatBytes(CONFIRMATION_MESSAGE_DOMAIN, contextBytes)),
  );
  if (!validTag) {
    throw new SessionHandshakeError(
      'confirmation-tag',
      'Transcript-bound key confirmation HMAC verification failed.',
    );
  }
  if (
    message.sessionId !== session.sessionId ||
    message.transcriptHash !== session.transcriptHash ||
    message.direction !== expectation.direction ||
    message.channel !== expectation.channel ||
    (expectation.sequence !== undefined && message.sequence !== expectation.sequence)
  ) {
    throw new SessionHandshakeError(
      'binding-mismatch',
      'Authenticated key confirmation does not match the expected session, transcript, or transport.',
    );
  }
  expectation.replayWindow?.accept(message.sequence);
  return message;
}
